import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/Api/axiosV2Config';
import type { ContractSummaryData } from '../utils/contractDetailFormat';
import type { ContractDispute } from '@/features/contracts/constants/dispute-labels';

export interface ContractListItem {
  id: number;
  label: string;
  idStatus: number;
  sellingPrice: number | null;
  monthlyPriceClient: number | null;
  monthlyPaymentId: string | null;
  /** État du dernier paiement (MONTHLY_PAYMENT_STATUS) — monthly ET comptant online. */
  monthlyPaymentStatus: string | null;
  beneficiary?: { firstName?: string; lastName?: string; email?: string } | null;
}

export interface PaymentDetails {
  paymentType: number;
  status: string;
  totalCount: number;
  paidCount: number;
  totalAmountTTC: number;
  paidAmount: number;
  remainingAmount: number;
  progressPercent: number;
  monthlyPrice: number;
  invoiceUrl: string | null;
  stripeInvoices: Array<{ url: string; number: string; amount: number; date: string }> | null;
}

/** Paramètres de liste (issus de useListState) mappés vers l'API contrats. */
export interface ContractsListParams {
  page: number;
  limit: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  searchTerm?: string;
  /** 'stripe' (défaut) | 'monthly' | 'online' */
  paymentType?: string;
  status?: string;
  /** Date de création minimale (YYYY-MM-DD), dérivée du filtre Période. */
  creationDateFrom?: string;
  minAmount?: string;
  maxAmount?: string;
}

/**
 * Critères de la liste au format de /pro-v2/contracts, hors pagination. Partagés
 * par la liste et son export : l'export porte exactement sur les contrats filtrés.
 *
 * `paymentType` par défaut = 'stripe' (exclut gratuit / comptant agence) ; le
 * filtre Mode le restreint à 'monthly'/'online' (sous-ensembles de 'stripe').
 */
const toContractsQueryParams = (
  societyId: string | undefined,
  filters: Omit<ContractsListParams, 'page' | 'limit'>,
) => ({
  societyId,
  paymentType: filters.paymentType || 'stripe',
  ...(filters.searchTerm ? { search: filters.searchTerm } : {}),
  ...(filters.status ? { status: filters.status } : {}),
  ...(filters.creationDateFrom ? { creationDateFrom: filters.creationDateFrom } : {}),
  ...(filters.minAmount ? { minAmount: filters.minAmount } : {}),
  ...(filters.maxAmount ? { maxAmount: filters.maxAmount } : {}),
  ...(filters.sortBy ? { sortBy: filters.sortBy } : {}),
  ...(filters.sortDirection ? { sortDirection: filters.sortDirection } : {}),
});

/**
 * Liste paginée + filtrée des contrats de la société (tête ⇒ tête + descendants
 * côté serveur). Réutilise l'endpoint existant, scopé et gardé.
 *
 * Le mapping traduit les noms de useListState (page/limit/searchTerm) vers ceux
 * de /pro-v2/contracts (skip/take/search).
 */
export const useSocietyContracts = (
  societyId: string | undefined,
  filters: ContractsListParams,
) =>
  useQuery({
    queryKey: ['stripe-admin', 'contracts', societyId, filters],
    queryFn: async () => {
      const { page, limit, ...criteria } = filters;
      const { data } = await axiosInstance.get<{ contracts: ContractListItem[]; total: number }>(
        '/pro-v2/contracts',
        {
          params: {
            ...toContractsQueryParams(societyId, criteria),
            skip: Math.max(0, (page - 1) * limit),
            take: limit,
          },
        },
      );
      return data;
    },
    enabled: !!societyId,
  });

/**
 * Export de l'onglet Contrats : la fiche de chaque contrat de la liste filtrée
 * (sans pagination), sans sa liste de paiements.
 */
export const fetchContractsPaymentSummary = async (
  societyId: string,
  filters: Omit<ContractsListParams, 'page' | 'limit'>,
): Promise<ContractSummaryData[]> => {
  const { data } = await axiosInstance.get<ContractSummaryData[]>(
    '/pro-v2/contracts/payments-summary',
    { params: toContractsQueryParams(societyId, filters) },
  );
  return data;
};

/**
 * Détail de paiement d'un contrat (échéances / paiement comptant).
 * NB : l'endpoint renvoie 400 si le contrat n'a pas de paiement Stripe
 * (monthlyPaymentId null) → on ne l'active QUE pour pi_/sub_/seti_.
 */
export const usePaymentDetails = (contractId: number | null, hasStripePayment: boolean) =>
  useQuery({
    queryKey: ['stripe-admin', 'payment-details', contractId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<PaymentDetails>(
        `/pro-v2/contracts/${contractId}/payment-details`,
      );
      return data;
    },
    enabled: contractId != null && hasStripePayment,
  });

/** Un paiement (échéance) d'un contrat. `paymentId` = charge id pour ConnectPaymentDetails. */
export interface ContractPaymentItem {
  paymentId: string | null;
  echeanceNumber: number | null;
  /** timestamp unix (secondes) */
  date: number;
  amount: number;
  /** statut invoice Stripe : paid | open | uncollectible | void | draft */
  status: string;
  combinedFee: number | null;
  net: number | null;
  hasReceipt: boolean;
  /** Prélèvement remboursé au client : hors numérotation des échéances et du montant payé. */
  refunded: boolean;
}

export interface ContractSubscriptionInfo {
  status: string;
  /** timestamp unix (secondes) — prochaine échéance */
  nextPaymentDate: number | null;
  cancelAt: number | null;
}

export interface ContractProgress {
  paidCount: number;
  totalCount: number;
  paidAmount: number;
  remainingAmount: number;
  totalAmountTTC: number;
  progressPercent: number;
}

export interface ContractPaymentsResponse {
  /** contrat enrichi (bénéficiaire, garantie, véhicule, garant…) */
  contract: any;
  subscription: ContractSubscriptionInfo | null;
  progress: ContractProgress;
  payments: ContractPaymentItem[];
  /** Litiges Stripe du contrat, du plus récent au plus ancien. */
  disputes?: ContractDispute[];
}

/** Vue détaillée d'un contrat : résumé + souscription + liste des paiements. */
export const useContractPayments = (contractId: number | null) =>
  useQuery({
    queryKey: ['stripe-admin', 'contract-payments', contractId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ContractPaymentsResponse>(
        `/pro-v2/contracts/${contractId}/payments`,
      );
      return data;
    },
    enabled: contractId != null,
  });
